<script lang="ts">
    import { createEventDispatcher } from 'svelte';

    import { PortletMenuActions } from '../types';

    interface Props {
        /** required for improved accessbility, used in aria-* attributes */
        uniqueId?: string;
        jumpToAOEnabled?: boolean;
        showSupportForm?: boolean;
        maximized?: boolean;
        externalInfoUrlEnabled?: boolean;
        isPerformanceLow?: boolean;
        isDataToDownload?: boolean;
    }

    let {
        uniqueId = 'menu',
        jumpToAOEnabled = false,
        showSupportForm = true,
        maximized = false,
        externalInfoUrlEnabled = false,
        isPerformanceLow = false,
        isDataToDownload = false,
    }: Props = $props();

    let locked = $state(false);

    const dispatch = createEventDispatcher();

    type Handler = (action: PortletMenuActions) => void;
    const onClick: Handler = action => dispatch('portlet-menu-item-click', action);

    const onMaximize = () => dispatch('portlet-maximize');
    const jumpToAO = () => dispatch('jump-to-ao');
    const openInfoUrl = () => dispatch('open-info-url');
    const onDownloadClick = () => dispatch('download-data');

    // metaKey is command on Mac, ctrlKey is control on Windows
    const onInfoClick = (e: MouseEvent) =>
        externalInfoUrlEnabled && !(e.ctrlKey || e.metaKey)
            ? openInfoUrl()
            : onClick(PortletMenuActions.INFO);

    let menuButtons = $derived([
        {
            action: () => onMaximize(),
            label: maximized ? 'Click to minimize the portlet' : 'Click to maximize the portlet',
            icon: maximized ? 'fullscreen_exit' : 'fullscreen',
            testId: 'menu-maximize',
        },
        {
            action: onDownloadClick,
            label: 'Click to download portlet data',
            icon: 'download',
            hidden: !isDataToDownload,
        },
        {
            action: () => jumpToAO(),
            label: 'Click to jump to AO',
            icon: 'file_open',
            hidden: !jumpToAOEnabled,
            testId: 'menu-jump-ao',
        },

        {
            action: () => onClick(PortletMenuActions.SETTINGS),
            label: 'Hover / click to show the portlet settings',
            icon: 'tune',
            testId: 'menu-settings',
        },
        {
            action: () => onClick(PortletMenuActions.FILTERS),
            label: 'Hover / click to show the portlet filters',
            icon: 'filter_alt',
            testId: 'menu-filters',
        },

        {
            action: () => onClick(PortletMenuActions.SUPPORT),
            label: 'Click to create a support request',
            icon: 'email',
            hidden: !showSupportForm,
            testId: 'menu-email',
        },
        {
            action: onInfoClick,
            label: 'Click to open the wiki page related to this portlet',
            icon: 'info',
            testId: 'menu-info',
        },
    ]);
</script>

{#if isPerformanceLow}
    <button
        class="performance-button"
        data-testid="menu-performance"
        title="Click to show portlet performance"
        aria-label="Click to show portlet performance"
        onclick={() => onClick(PortletMenuActions.PERFORMANCE)}
    >
        <i class="material-icons">network_check</i>
    </button>
{/if}
<div class="portlet-menu" data-testid="portlet-menu" class:portlet-menu--locked={locked}>
    <button
        id="toggle-button-{uniqueId}"
        data-testid="portlet-menu-button"
        class="portlet-menu__toggle-button portlet-menu__button"
        aria-haspopup="true"
        aria-controls="menu-list-{uniqueId}"
        aria-label="Open portlet menu"
        onclick={() => (locked = !locked)}
    >
        <i class="material-icons">add_circle_outline</i>
    </button>
    <ul
        id="menu-list-{uniqueId}"
        class="portlet-menu__button-list button-list"
        role="menu"
        aria-labelledby="toggle-button-{uniqueId}"
    >
        {#each menuButtons as item}
            {#if !item.hidden}
                <li role="none" class="button-list__item">
                    <button
                        tabindex={locked ? 0 : -1}
                        class="button-list__button portlet-menu__button"
                        data-testid={item.testId}
                        title={item.label}
                        aria-label={item.label}
                        onclick={item.action}
                    >
                        <i class="material-icons">{item.icon}</i>
                    </button>
                </li>
            {/if}
        {/each}
    </ul>
</div>

<style lang="scss">
    @use '../../common/styles/mixins.scss' as m;

    $button-size: 32px;
    $gap: 4px;
    $num-of-items: 10;
    $list-width-expanded: 200px;
    $icon-color: #505050;
    $icon-background: #fafafa;
    $icon-background-active: darken($icon-background, 10%);

    .portlet-menu {
        position: relative;
        z-index: 1;

        &--locked {
            .portlet-menu__button-list {
                width: $list-width-expanded;
            }
        }

        &__button-list {
            height: 100%;
            width: $button-size;

            &:hover {
                width: $list-width-expanded;
            }
        }

        &__button {
            @include m.reset-button;

            width: $button-size;
            height: $button-size;
            padding: 4px;

            border-radius: $button-size;
            color: var(--color, $icon-color);
            background-color: var(--background-color, $icon-background);

            &:hover {
                background-color: $icon-background-active;
            }
        }

        &__toggle-button:before {
            content: ' ';
            position: absolute;
            top: 0;

            right: 0;
            width: $button-size + $gap;
            height: 100%;
        }
    }

    .button-list {
        @include m.reset-ul;

        display: flex;
        position: absolute;
        top: 0;
        right: 0;
        z-index: -1;

        &__item {
            position: absolute;
            z-index: -1;
            right: 0;

            visibility: hidden;
            background-color: var(--background-color, white);
            transition:
                visibility 0.2s,
                transform 0.2s ease-out,
                opacity 0.2s ease-out;
        }

        &__button {
            margin-left: $gap;
        }
    }

    .portlet-menu--locked .button-list__item,
    .portlet-menu__button-list:hover .button-list__item,
    .portlet-menu__toggle-button:hover ~ * .button-list__item {
        @for $i from 1 through $num-of-items {
            &:nth-child(#{$i}) {
                transform: translateX(-$i * ($button-size + $gap));
                visibility: visible;
            }
        }
    }
    .performance-button {
        @include m.reset-button;

        width: $button-size;
        height: $button-size;
        padding: 4px;
        color: #f4a103;
        border-radius: $button-size;
        background-color: var(--background-color, $icon-background);

        &:hover {
            cursor: pointer;
            background-color: $icon-background-active;
        }
    }
</style>
